import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import apiService from '../../services/api';
import { useToast } from '../../hooks/useToast';
import {
  formatAbsenceBlock,
  getDateExceptionPlagesForYmd,
} from '../../utils/equipePlanningUtils';
import { formatParisDayHeader } from '../../utils/equipeWeekParis';
import {
  DISPLAY_KIND,
  getDayPlanningDisplayState,
} from '../../utils/equipeDayPlanningDisplayState';
import EquipeDateHoursForm from './EquipeDateHoursForm';

export default function EquipePlanningDateDetail({
  open,
  onClose,
  member,
  day,
  horairesConfig,
  absences = [],
  dateExceptionsByDate = null,
  rdvCount = 0,
  onSaved,
}) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [showHoursForm, setShowHoursForm] = useState(false);

  const instructeurId = member?.instructeurId;
  const ymd = day?.ymd;
  const jourSemaine = day?.jourSemaine;
  const dateLabel = day?.date ? formatParisDayHeader(day.date) : '';

  const display = useMemo(
    () =>
      getDayPlanningDisplayState({
        horairesConfig,
        absences,
        jourSemaine,
        ymd,
        dateExceptionsByDate,
      }),
    [horairesConfig, absences, jourSemaine, ymd, dateExceptionsByDate],
  );

  const overridePlages = getDateExceptionPlagesForYmd(dateExceptionsByDate, ymd);
  const hasHoursOverride = overridePlages !== undefined;

  if (!open || !member || !day) return null;

  const initialHoursPlages =
    hasHoursOverride && overridePlages?.length
      ? overridePlages.map((p) => ({
          heureDebut: p.heureDebut || '09:00',
          heureFin: p.heureFin || '17:00',
        }))
      : [{ heureDebut: '10:00', heureFin: '19:00' }];

  const saveHours = async (plages) => {
    if (!instructeurId || !ymd) return;
    if (!plages.length) {
      toast.error('Ajoutez au moins une plage horaire');
      return;
    }
    setSaving(true);
    try {
      await apiService.upsertInstructeurHoraireDateException(instructeurId, ymd, plages);
      toast.success('Planning de la journée enregistré');
      setShowHoursForm(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const markRest = async () => {
    if (!instructeurId || !ymd) return;
    const ok = window.confirm(
      `${member.prenom} sera en repos le ${dateLabel.toLowerCase()} uniquement.\nSon planning habituel reste inchangé.`,
    );
    if (!ok) return;
    setSaving(true);
    try {
      await apiService.upsertInstructeurHoraireDateException(instructeurId, ymd, []);
      toast.success('Repos enregistré pour cette date');
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Action impossible');
    } finally {
      setSaving(false);
    }
  };

  const restoreBase = async () => {
    if (!instructeurId || !ymd || !hasHoursOverride) return;
    setSaving(true);
    try {
      await apiService.deleteInstructeurHoraireDateException(instructeurId, ymd);
      toast.success('Planning de base rétabli pour cette date');
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Action impossible');
    } finally {
      setSaving(false);
    }
  };

  const showBaseBlock =
    display.kind === DISPLAY_KIND.CUSTOM_HOURS ||
    display.kind === DISPLAY_KIND.REST ||
    (display.baseLabel && !display.conformsToBase);

  const canEdit = member.isIntervenant && instructeurId;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-slate-900/20"
        aria-label="Fermer"
        onClick={onClose}
      />
      <aside className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto rounded-t-2xl border border-gray-100 bg-white shadow-2xl sm:inset-y-auto sm:left-auto sm:right-4 sm:top-20 sm:max-h-[calc(100vh-5rem)] sm:w-[min(440px,92vw)] sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {member.prenom} {member.nom}
            </h2>
            <p className="text-sm text-gray-700">{dateLabel}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-4 py-4 text-sm">
          {display.kind === DISPLAY_KIND.LEAVE || display.kind === DISPLAY_KIND.ABSENT ? (
            <section className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-3">
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">Planning prévu</h3>
              <p className="mt-1 text-base font-semibold text-amber-950">{display.plannedLabel}</p>
              {display.dayAbsences?.map((a) => {
                const { title, detail } = formatAbsenceBlock(a.dateDebut, a.dateFin, a.motif);
                return (
                  <p key={a.id} className="mt-2 text-xs text-amber-900">
                    {title}
                    {detail ? ` — ${detail}` : ''}
                  </p>
                );
              })}
            </section>
          ) : (
            <>
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">Planning prévu</h3>
                <p className="mt-1 text-base font-semibold text-gray-900">{display.plannedLabel}</p>
                {display.conformsToBase ? (
                  <p className="mt-1 text-xs text-gray-500">Conforme au planning de base</p>
                ) : null}
              </section>

              {showBaseBlock && display.baseLabel ? (
                <section>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">Planning de base</h3>
                    {!display.conformsToBase ? (
                      <span className="inline-block rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800">
                        Exception pour cette date
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-gray-800">{display.baseLabel}</p>
                </section>
              ) : null}
            </>
          )}

          {display.kind === DISPLAY_KIND.UNAVAILABLE && display.baseLabel ? (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">Planning de base</h3>
              <p className="mt-1 text-gray-800">{display.baseLabel}</p>
            </section>
          ) : null}

          {canEdit && !showHoursForm ? (
            <div className="space-y-2 border-t border-gray-100 pt-4">
              <button
                type="button"
                disabled={saving}
                onClick={() => setShowHoursForm(true)}
                className="w-full rounded-lg bg-violet-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
              >
                Modifier les horaires
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={markRest}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
              >
                Mettre en repos
              </button>
              {hasHoursOverride ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={restoreBase}
                  className="w-full rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-xs font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-60"
                >
                  Revenir au planning de base
                </button>
              ) : null}
            </div>
          ) : null}

          {canEdit && showHoursForm ? (
            <EquipeDateHoursForm
              dateLabel={dateLabel}
              initialPlages={initialHoursPlages}
              saving={saving}
              onSubmit={saveHours}
              onCancel={() => setShowHoursForm(false)}
            />
          ) : null}

          {(display.kind === DISPLAY_KIND.LEAVE ||
            display.kind === DISPLAY_KIND.ABSENT ||
            display.kind === DISPLAY_KIND.UNAVAILABLE) && (
            <Link
              to="/equipe/presences"
              className="inline-block text-xs font-semibold text-violet-700 hover:underline"
              onClick={onClose}
            >
              Gérer absences et indisponibilités dans Présences
            </Link>
          )}

          <div className="border-t border-gray-100 pt-3">
            <Link
              to={`/equipe?member=${encodeURIComponent(member.id)}`}
              className="text-xs font-medium text-gray-500 hover:text-violet-700 hover:underline"
              onClick={onClose}
            >
              Fiche complète
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
}
